import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Progress, Spin } from 'antd';
import Table from 'components/Common/Table';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  TeamOutlined,
  RiseOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { format } from 'date-fns';

const SupportAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/support/analytics/comprehensive/');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <p>Failed to load analytics data</p>
        </Card>
      </div>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Prepare chart data
  const statusData = analytics.ticket_volume?.by_status?.map((item) => ({
    name: item.status__name,
    value: item.count
  })) || [];

  const priorityData = analytics.ticket_volume?.by_priority?.map((item) => ({
    name: item.priority,
    value: item.count
  })) || [];

  const dailyData = analytics.ticket_volume?.daily_tickets?.map((item) => ({
    date: format(new Date(item.date), 'MMM dd'),
    tickets: item.count
  })) || [];

  const ratingData = Object.entries(analytics.customer_satisfaction?.rating_distribution || {}).map(([rating, count]) => ({
    rating: `${rating} ⭐`,
    count
  }));

  // Employee performance columns
  const employeeColumns = [
    {
      title: 'Employee',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Total Tickets',
      dataIndex: 'total_tickets',
      key: 'total',
      sorter: (a, b) => a.total_tickets - b.total_tickets
    },
    {
      title: 'Open',
      dataIndex: 'open_tickets',
      key: 'open',
      sorter: (a, b) => a.open_tickets - b.open_tickets
    },
    {
      title: 'Closed',
      dataIndex: 'closed_tickets',
      key: 'closed',
      sorter: (a, b) => a.closed_tickets - b.closed_tickets
    },
    {
      title: 'Avg Rating',
      dataIndex: 'average_rating',
      key: 'rating',
      render: (rating) => rating > 0 ? (
        <span>
          {rating} <StarOutlined style={{ color: '#faad14' }} />
        </span>
      ) : '-',
      sorter: (a, b) => a.average_rating - b.average_rating
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: 24 }}>Support Analytics & Reports</h1>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tickets"
              value={analytics.ticket_volume?.total_tickets || 0}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg First Response"
              value={analytics.response_times?.average_first_response_hours || 0}
              suffix="hrs"
              precision={1}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Resolution Time"
              value={analytics.response_times?.average_resolution_hours || 0}
              suffix="hrs"
              precision={1}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Customer Satisfaction"
              value={analytics.customer_satisfaction?.average_rating || 0}
              suffix="/ 5"
              precision={2}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* SLA Compliance */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="SLA Compliance" extra={
            <span style={{ fontSize: 24, fontWeight: 'bold', color: analytics.sla_compliance?.compliance_rate >= 90 ? '#52c41a' : '#faad14' }}>
              {analytics.sla_compliance?.compliance_rate || 0}%
            </span>
          }>
            <Row gutter={16}>
              <Col span={8}>
                <Card bordered={false}>
                  <Statistic
                    title="Within SLA"
                    value={analytics.sla_compliance?.within_sla || 0}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card bordered={false}>
                  <Statistic
                    title="Approaching Breach"
                    value={analytics.sla_compliance?.approaching_breach || 0}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card bordered={false}>
                  <Statistic
                    title="Breached"
                    value={analytics.sla_compliance?.breached || 0}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={analytics.sla_compliance?.compliance_rate || 0}
                status={analytics.sla_compliance?.compliance_rate >= 90 ? 'success' : 'normal'}
                strokeColor={analytics.sla_compliance?.compliance_rate >= 90 ? '#52c41a' : '#faad14'}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* Daily Ticket Volume */}
        <Col span={12}>
          <Card title="Daily Ticket Volume (Last 30 Days)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tickets" stroke="#1890ff" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Tickets by Status */}
        <Col span={12}>
          <Card title="Tickets by Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* Tickets by Priority */}
        <Col span={12}>
          <Card title="Tickets by Priority">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Customer Satisfaction Ratings */}
        <Col span={12}>
          <Card title="Customer Satisfaction Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ratingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rating" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#faad14" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Statistic
                title="Satisfaction Rate (4-5 stars)"
                value={analytics.customer_satisfaction?.satisfaction_rate || 0}
                suffix="%"
                precision={1}
                valueStyle={{
                  color: analytics.customer_satisfaction?.satisfaction_rate >= 80 ? '#52c41a' : '#faad14'
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Employee Performance */}
      <Row gutter={16}>
        <Col span={24}>
          <Card title={<span><TeamOutlined /> Employee Performance</span>}>
            <Table
              columns={employeeColumns}
              dataSource={analytics.employee_performance?.by_employee || []}
              rowKey="employee_id"
              pagination={false}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SupportAnalytics;
